/**
 * Payment methods on the ORGANIZATION account — the school counterpart of the
 * family wallet's `PaymentMethodsCard` (PROD-127).
 *
 * Deliberately NOT the same component. The family card is half card-sharing
 * machinery — a two-step add flow asking which guardians may charge the card, a
 * sharing editor, per-guardian remove gating — and none of that exists for an
 * org: its cards are usable by every SCHOOL_ADMIN on the org by design, so
 * there is nobody to share with. What genuinely IS shared stays shared:
 * `describeCard` / `validateTestCard` from ./helpers, and `StripeElementsForm`.
 *
 * Renders on the Balance (PREPAID) view only, because that is the only place an
 * org card is ever charged — the invoice view pays through the Stripe-hosted
 * invoice URL or a self-reported check, neither of which touches a stored
 * method.
 */

import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ordo/ui/card'
import { Input } from '@ordo/ui/input'
import { Label } from '@ordo/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { SimpleDialog } from '@ordo/ui/simple-dialog'
import { toast } from '@ordo/ui/sonner'
import { useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, CreditCard, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { type BillingPaymentMethod, useBillingConfigQuery } from '@/api/billing-family'
import {
  schoolBillingAccountQueryKey,
  useCreateSchoolTestPaymentMethodMutation,
  useRemoveSchoolPaymentMethodMutation,
  useSchoolSetupIntentMutation,
  useSetSchoolDefaultPaymentMethodMutation,
} from '@/api/billing-school-account'
import { COMPACT_TAG_WITH_ICON_CLASS } from '@/lib/tag-classes'

import { describeCard, validateTestCard } from './helpers'
import { StripeElementsForm } from './stripe-elements-lazy'

const BRAND_OPTIONS = [
  { label: 'Visa', value: 'visa' },
  { label: 'Mastercard', value: 'mastercard' },
  { label: 'American Express', value: 'amex' },
  { label: 'Discover', value: 'discover' },
]

export interface SchoolPaymentMethodsCardProps {
  /** The campus the SCHOOL_ADMIN gate checks; the account itself is org-level. */
  schoolLocationId: string
  paymentMethods: BillingPaymentMethod[]
}

export function SchoolPaymentMethodsCard({ schoolLocationId, paymentMethods }: SchoolPaymentMethodsCardProps) {
  const [testCardOpen, setTestCardOpen] = useState(false)
  const [setupClientSecret, setSetupClientSecret] = useState<string | null>(null)
  const [brand, setBrand] = useState('visa')
  const [last4, setLast4] = useState('4242')
  const [expMonth, setExpMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'))
  const [expYear, setExpYear] = useState(String(new Date().getFullYear() + 3))

  const qc = useQueryClient()
  const config = useBillingConfigQuery()
  const stripeConfigured = Boolean(config.data?.stripePublishableKey)
  // Until the config query settles, `stripeConfigured` is false for "not loaded
  // yet", not for "no Stripe" — clicking Add in that window would open the
  // local test-card dialog against a production account. Same guard, and the
  // same reasoning, as TopUpSheet's `configReady`.
  const configReady = config.isSuccess
  const setupIntentMutation = useSchoolSetupIntentMutation()
  const createTestCardMutation = useCreateSchoolTestPaymentMethodMutation(schoolLocationId)
  const setDefaultMutation = useSetSchoolDefaultPaymentMethodMutation(schoolLocationId)
  const removeMutation = useRemoveSchoolPaymentMethodMutation(schoolLocationId)
  const loading =
    !configReady ||
    setupIntentMutation.isPending ||
    createTestCardMutation.isPending ||
    setDefaultMutation.isPending ||
    removeMutation.isPending

  // Stripe configured → collect a real card via a SetupIntent + Elements.
  // Not configured (local dev / preview envs) → the test-card form below.
  const handleAddClick = async () => {
    if (!stripeConfigured) {
      setTestCardOpen(true)
      return
    }
    try {
      const intent = await setupIntentMutation.mutateAsync(schoolLocationId)
      if (!intent.clientSecret) {
        toast.error('Could not start card setup', { description: 'No setup session was returned.' })
        return
      }
      setSetupClientSecret(intent.clientSecret)
    } catch (err) {
      toast.error('Could not start card setup', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  const handleSaveTestCard = async () => {
    const card = validateTestCard({ last4, expMonth, expYear }, new Date())
    if (!card.ok) {
      toast.error(card.message)
      return
    }
    try {
      await createTestCardMutation.mutateAsync({
        schoolLocationId,
        brand,
        last4: card.last4,
        expMonth: card.expMonth,
        expYear: card.expYear,
      })
      setTestCardOpen(false)
      toast.success('Payment method added')
    } catch (err) {
      toast.error('Could not add payment method', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  const handleSetDefault = async (method: BillingPaymentMethod) => {
    try {
      await setDefaultMutation.mutateAsync({ schoolLocationId, paymentMethodId: method.stripePaymentMethodId })
      toast.success('Default payment method updated')
    } catch (err) {
      toast.error('Could not update default method', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  const handleRemove = async (method: BillingPaymentMethod) => {
    try {
      await removeMutation.mutateAsync({ schoolLocationId, paymentMethodId: method.stripePaymentMethodId })
      toast.success('Payment method removed')
    } catch (err) {
      toast.error('Could not remove payment method', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Payment methods</CardTitle>
          <CardDescription className="text-xs leading-relaxed">
            Saved for this organization — every school admin here can add funds with them.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col">
          {paymentMethods.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
              <CreditCard className="h-7 w-7 text-muted-foreground" aria-hidden />
              <p className="text-sm font-medium text-foreground">No payment methods saved yet</p>
              <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
                Add a card below and it's ready the next time this school adds funds — or save a bank account while
                adding funds.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {paymentMethods.map((method) => (
                <div key={method.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5">
                  <div className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
                    <CreditCard className="h-4 w-4" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex items-center gap-1.5">
                      <p className="min-w-0 truncate text-sm font-medium text-foreground">{describeCard(method)}</p>
                      {method.isDefault ? (
                        <Badge variant="secondary" className={COMPACT_TAG_WITH_ICON_CLASS}>
                          <CheckCircle2 className="h-2.5 w-2.5" aria-hidden />
                          Default
                        </Badge>
                      ) : null}
                    </div>
                    {/* Wallets (Apple Pay / Google Pay) carry no expiry — the
                        line is omitted rather than repeating the card label. */}
                    {method.expMonth && method.expYear ? (
                      <span className="text-xs text-muted-foreground">
                        Expires {String(method.expMonth).padStart(2, '0')}/{method.expYear}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {!method.isDefault ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        onClick={() => void handleSetDefault(method)}
                        disabled={loading}>
                        Make default
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      onClick={() => void handleRemove(method)}
                      disabled={loading}
                      aria-label={`Remove ${describeCard(method)}`}>
                      <Trash2 className="h-3 w-3" aria-hidden />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Dashed foot-of-list affordance, matching the family card. */}
          <button
            type="button"
            onClick={() => void handleAddClick()}
            disabled={loading}
            className="mt-1.5 flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:bg-muted/30 hover:text-foreground disabled:opacity-50">
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add payment method
          </button>
        </CardContent>
      </Card>

      <SimpleDialog
        open={testCardOpen}
        onClose={() => setTestCardOpen(false)}
        title="Add payment method"
        description="Add a test card for this local billing flow."
        maxWidth="md">
        <form
          className="contents"
          onSubmit={(e) => {
            e.preventDefault()
            void handleSaveTestCard()
          }}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="school-payment-method-brand">Card brand</Label>
              <Select value={brand} onValueChange={setBrand} disabled={loading}>
                <SelectTrigger id="school-payment-method-brand">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BRAND_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="flex flex-col gap-2">
                <Label htmlFor="school-payment-method-last4">Last 4</Label>
                <Input
                  id="school-payment-method-last4"
                  inputMode="numeric"
                  maxLength={4}
                  value={last4}
                  disabled={loading}
                  onChange={(event) => setLast4(event.target.value.replace(/\D/g, '').slice(0, 4))}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="school-payment-method-exp-month">Exp. month</Label>
                <Input
                  id="school-payment-method-exp-month"
                  inputMode="numeric"
                  maxLength={2}
                  value={expMonth}
                  disabled={loading}
                  onChange={(event) => setExpMonth(event.target.value.replace(/\D/g, '').slice(0, 2))}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="school-payment-method-exp-year">Exp. year</Label>
                <Input
                  id="school-payment-method-exp-year"
                  inputMode="numeric"
                  maxLength={4}
                  value={expYear}
                  disabled={loading}
                  onChange={(event) => setExpYear(event.target.value.replace(/\D/g, '').slice(0, 4))}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setTestCardOpen(false)} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {createTestCardMutation.isPending ? 'Adding...' : 'Add payment method'}
              </Button>
            </div>
          </div>
        </form>
      </SimpleDialog>

      <SimpleDialog
        open={Boolean(setupClientSecret)}
        onClose={() => setSetupClientSecret(null)}
        title="Add a payment method"
        description="The card is saved securely by Stripe against this organization, ready for the next time you add funds."
        maxWidth="md">
        {setupClientSecret ? (
          <StripeElementsForm
            clientSecret={setupClientSecret}
            mode="setup"
            submitLabel="Save payment method"
            onSuccess={() => {
              setSetupClientSecret(null)
              // The mirror row is written by the `payment_method.attached`
              // webhook, so the refetch can land a beat before the row does —
              // hence the "in a moment" wording rather than a silent empty list.
              void qc.invalidateQueries({ queryKey: schoolBillingAccountQueryKey(schoolLocationId) })
              toast.success('Payment method saved', { description: 'It will appear here in a moment.' })
            }}
            onCancel={() => setSetupClientSecret(null)}
          />
        ) : null}
      </SimpleDialog>
    </>
  )
}
